import React from 'react';
/* eslint-disable-next-line restrict-imports/restrict-imports */
import Autocomplete, { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete';
import { useQuery } from 'react-query';
import { CircularProgress } from 'react-scale/components/design-system';
import { FindUserJSON } from '@scale/shared/types/api/corp-api/find';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import TextInput from 'design-system/TextInput';
import { useThrottle } from 'react-use-v13';
import { parseObjectIds } from 'react-scale/corp/lib/parseInputText';

interface SelectCustomerV2Props {
  value: FindUserJSON | null;
  onChange: (value: FindUserJSON | null) => void;
  defaultValue?: any;
}

const SelectCustomerV2: React.FC<SelectCustomerV2Props> = props => {
  const { value, onChange, defaultValue } = props;

  const [open, setOpen] = React.useState<boolean>(false);

  const [inputValue, setInputValue] = React.useState<string>('');

  const onInputChange = (_event: unknown, value: string) => {
    if (value) {
      onChange(null);
    }
    setInputValue(value);
  };

  const onSelectChange = (_event: unknown, value: FindUserJSON | null) => {
    onChange(value);
  };

  const searchText = useThrottle(inputValue, 450);

  const findQueryResult = useQuery(
    ['corp-search-customer', searchText],
    () => {
      return findCustomers(searchText);
    },
    {
      keepPreviousData: true,
    },
  );

  const loading = findQueryResult.isFetching && !value;

  const options = findQueryResult.data ?? [defaultValue];

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        if (!value) {
          setOpen(true);
        }
      }}
      onClose={() => {
        setOpen(false);
      }}
      options={!value ? options : []}
      loading={loading}
      renderInput={(params: AutocompleteRenderInputParams) => {
        return (
          <TextInput
            {...params}
            placeholder="Enter customer id or email ..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
              style: {
                paddingTop: 4,
                paddingBottom: 4,
              },
            }}
            size="medium"
            fullWidth
          />
        );
      }}
      getOptionLabel={(option: FindUserJSON) => {
        return `${option.email} (${option.id})`;
      }}
      onChange={onSelectChange}
      inputValue={inputValue}
      onInputChange={onInputChange}
      disableClearable={!value}
      defaultValue={defaultValue}
      filterOptions={
        /* show all the options from the api, avoid filtering more */
        x => x
      }
    />
  );
};

SelectCustomerV2.displayName = 'SelectCustomerV2';

const findCustomers = async (text: string): Promise<FindUserJSON[]> => {
  try {
    if (text) {
      const query: Record<string, string> = {};

      // Support for object ids.
      const ids = parseObjectIds(text);
      if (ids.length) {
        query.objectId = ids[0];
      } else {
        query.search = text;
      }

      const { users } = await apiFetch<{ users: FindUserJSON[] }>(null, 'corp-api/find/customers', {
        query,
      });

      return users;
    }
  } catch (error) {
    defaultApiFetchErrorHandler(error as $FIXME, { handleHttp: true });
  }
  return [];
};

export default SelectCustomerV2;
